import type { SocialPlatform } from "@/types/account";
import type { AssistantPlatformPosts } from "@/types/assistant";

export function getAssistantsPostsQueryKey(platform: SocialPlatform, id: string) {
  return ["assistant-social-posts", platform, id];
}
export function useAssistantSocialPostRequest(id: string, platform: SocialPlatform) {
  const query = useInfiniteQuery({
    queryKey: getAssistantsPostsQueryKey(platform, id),
    queryFn: async ({ pageParam }: { pageParam?: string } = {}) => {
      const response = await useGetRequest<{
        items: AssistantPlatformPosts[typeof platform];
        next?: string;
      }>("/account/data", {
        params: {
          itemId: id,
          getSubItems: true,
          next: pageParam ? pageParam : undefined,
        },
      });
      const responseData = response.data || null;
      if (responseData?.items) {
        responseData.items = responseData.items.filter(
          // @ts-ignore
          item => !item.sourceData?.currentProcessingJobId
        ) as AssistantPlatformPosts[typeof platform];

        if (platform === "linkedin") {
          responseData.items = filterLinkedInPosts(
            responseData.items as AssistantPlatformPosts["linkedin"]
          ) as AssistantPlatformPosts[typeof platform];
        }
      }
      return responseData;
    },
    initialPageParam: "",
    getNextPageParam: lastPage => lastPage?.next || null,
    select: data => {
      return data.pages.map(page => page.items).flat();
    },
    staleTime: 3 * MINUTE,
  });
  return query;
}

function filterLinkedInPosts(
  posts: AssistantPlatformPosts["linkedin"]
): AssistantPlatformPosts["linkedin"] {
  const formatted = posts
    .map(post => {
      return {
        ...post,
        sourceData: {
          ...post.sourceData,
          videos: "", // prevent video player from showing up
        },
      };
    })
    // provent vide-only posts from passing through
    .filter(
      post =>
        !!post.sourceData?.text ||
        !!post.sourceData?.images?.length ||
        !!post.sourceData.title
    );
  return formatted;
}

export function useAssistantSocialShortVideoRequest(
  id: string,
  platform: SocialPlatform
) {
  const query = useInfiniteQuery({
    queryKey: ["assistant-social-short-videos", platform, id],
    queryFn: async ({ pageParam }: { pageParam?: string } = {}) => {
      const response = await useGetRequest<{
        items: AssistantPlatformPosts[typeof platform];
        next?: string;
      }>("/account/data", {
        params: {
          itemId: id,
          getSubItems: true,
          next: pageParam ? pageParam : undefined,
        },
      });
      return response.data || null;
    },
    initialPageParam: "",
    getNextPageParam: lastPage => lastPage?.next || null,
    select: data => {
      return data.pages.map(page => page.items).flat();
    },
    staleTime: 3 * MINUTE,
    enabled: platform === "facebook",
  });
  return query;
}
